var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { computed, observable, runInAction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { getObservableT, i18nGetCurrentLanguage } from "../Infra/i18next";
import { buildWebsiteUrl, WebsiteURL } from "healthex-common/websiteurls";
import { FormModel } from "./FormModel";
import { NamedRoutes } from "./NamedRoutes";
import { HXMenuController } from "./HXMenu";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { GFIDProvider } from "healthex-common/healthcareorganization.dtos";
const t = getObservableT("patients");
export class PatientProfileViewModel {
    constructor(container) {
        this.formModel = new FormModel({ selectedContactId: null }, []);
        this.smsOptInDialogModel = new SimpleDialogModel();
        this.smsOptInConfirmDialogModel = new SimpleDialogModel();
        this.profileMoreMenuController = new HXMenuController();
        this.smsOptInPollApiTimerId = null;
        this.smsOptInDialogOpenLimitTimerId = null;
        this.pendingPhoneForSmsOptIn = null;
        this.isSavingContactSelection = false;
        this.isAvatarUploading = false;
        this.authManager = container.authManager;
        this.routingUIManager = container.routingUIManager;
        this.appUIManager = container.appUIManager;
        this.patientContactsUIManager = container.patientContactsUIManager;
        this.patientPreferencesManager = container.patientPreferencesManager;
        this.bannerUIManager = container.bannerUIManager;
        this.environment = container.environment;
        this.patientService = container.patientService;
        this.provisioningService = container.provisioningService;
        this.embeddedManager = container.embeddedManager;
        this.formModel.onInputUpdate = (update) => {
            if ("selectedContactId" in update && update.selectedContactId) {
                void this.saveContactSelection(update.selectedContactId);
            }
        };
    }
    run() { }
    stop() {
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
    }
    get showLogout() {
        return !this.embeddedManager.embedded;
    }
    get orgName() {
        return this.authManager.orgName;
    }
    get name() {
        return this.authManager.name;
    }
    get userForAvatar() {
        return this.authManager.userForAvatar;
    }
    get email() {
        return this.authManager.email;
    }
    get phone() {
        return this.authManager.formattedPhone;
    }
    get preferredContactMethodRemote() {
        return this.patientContactsUIManager.preferredContactMethod;
    }
    get isOptedIn() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return !((_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.optedOut) !== null && _b !== void 0 ? _b : false);
    }
    get smsOptedOut() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return false;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.smsOptedOut) !== null && _b !== void 0 ? _b : false;
    }
    get phoneNumberSuffix() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.phoneNumberSuffix) !== null && _b !== void 0 ? _b : null;
    }
    get twilioFromPhoneNumber() {
        var _a, _b;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return null;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.twilioFromPhoneNumber) !== null && _b !== void 0 ? _b : null;
    }
    get showPreferredContacts() {
        if (this.isOptedIn === NOT_LOADED)
            return NOT_LOADED;
        return this.isOptedIn && (!!this.email || !!this.phone);
    }
    get isClearLogin() {
        return this.authManager.gfidProvider === GFIDProvider.CLEAR;
    }
    get contactsLoading() {
        return this.patientPreferencesManager.preferencesWithContacts.value === NOT_LOADED;
    }
    get emails() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return [...((_a = prefs.emails) !== null && _a !== void 0 ? _a : [])].sort((a, b) => a.id.localeCompare(b.id));
    }
    get phones() {
        var _a;
        const prefs = this.patientPreferencesManager.preferencesWithContacts.value;
        if (prefs === NOT_LOADED || !prefs)
            return [];
        return [...((_a = prefs.phones) !== null && _a !== void 0 ? _a : [])].sort((a, b) => a.id.localeCompare(b.id));
    }
    get hasMultipleContacts() {
        return this.emails.length + this.phones.length > 1;
    }
    get currentSelectedContactId() {
        if (this.formModel.input.selectedContactId !== null) {
            return this.formModel.input.selectedContactId;
        }
        const primaryEmail = this.emails.find((e) => e.isPrimary);
        if (primaryEmail && this.preferredContactMethodRemote === ContactMethod.EMAIL) {
            return primaryEmail.id;
        }
        const primaryPhone = this.phones.find((p) => p.isPrimary);
        if (primaryPhone && this.preferredContactMethodRemote === ContactMethod.TEXT) {
            return primaryPhone.id;
        }
        if (primaryEmail)
            return primaryEmail.id;
        if (primaryPhone)
            return primaryPhone.id;
        return null;
    }
    selectContact(contactId) {
        if (contactId === this.currentSelectedContactId || this.isSavingContactSelection)
            return;
        this.formModel.updateInput({ selectedContactId: contactId });
    }
    async saveContactSelection(contactId) {
        const selectedEmail = this.emails.find((e) => e.id === contactId);
        const selectedPhone = this.phones.find((p) => p.id === contactId);
        if (!selectedEmail && !selectedPhone)
            return;
        if (selectedPhone && this.smsOptedOut) {
            await this.showSmsOptInDialogForContactSelection(selectedPhone);
            return;
        }
        runInAction(() => {
            this.isSavingContactSelection = true;
        });
        try {
            if (selectedEmail) {
                await this.patientPreferencesManager.setPrimaryEmail(selectedEmail.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.EMAIL });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{email}}", { email: selectedEmail.email }),
                });
            }
            else if (selectedPhone) {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.TEXT });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{phone}}", { phone: selectedPhone.phoneNumber }),
                });
            }
        }
        catch (e) {
            console.error("Failed to save contact selection", e);
            this.bannerUIManager.displayBanner({
                text: t("Failed to update your contact method. Please try again"),
                type: "ERROR",
            });
        }
        finally {
            runInAction(() => {
                this.formModel.updateInput({ selectedContactId: null }, false);
                this.isSavingContactSelection = false;
            });
        }
    }
    async showSmsOptInDialogForContactSelection(selectedPhone) {
        this.pendingPhoneForSmsOptIn = selectedPhone;
        this.smsOptInDialogModel.show();
        this.smsOptInPollApi();
        this.smsOptInDialogOpenLimit();
        const confirmed = await this.smsOptInDialogModel.showAndWait();
        if (this.smsOptInConfirmDialogModel.open)
            return;
        await this.smsOptInDialogClosedForContactSelection(confirmed);
    }
    async showSmsOptInConfirmDialog() {
        this.smsOptInConfirmDialogModel.show();
        if (this.smsOptInDialogModel.open)
            this.smsOptInDialogModel.onChoice(false);
        const confirmed = await this.smsOptInConfirmDialogModel.showAndWait();
        await this.smsOptInDialogClosedForContactSelection(confirmed);
    }
    async smsOptInDialogClosedForContactSelection(isSuccess) {
        const selectedPhone = this.pendingPhoneForSmsOptIn;
        this.clearSmsOptInPollApi();
        this.clearSmsOptInDialogOpenLimit();
        this.smsOptInDialogModel.open = false;
        this.pendingPhoneForSmsOptIn = null;
        if (isSuccess && selectedPhone) {
            try {
                await this.patientPreferencesManager.setPrimaryPhone(selectedPhone.id);
                await this.patientPreferencesManager.updatePatientPreferences({ preferredContactMethod: ContactMethod.TEXT });
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method has been updated to {{phone}}", { phone: selectedPhone.phoneNumber }),
                });
            }
            catch (e) {
                console.error("Failed to update preferred contact method", e);
                this.bannerUIManager.displayBanner({
                    text: t("Your contact method was unable to be updated to Phone. Please try again"),
                    type: "ERROR",
                });
            }
        }
        else {
            this.bannerUIManager.displayBanner({
                text: t("Your contact method was unable to be updated to Phone. Please try again"),
                type: "ERROR",
            });
        }
        runInAction(() => {
            this.formModel.updateInput({ selectedContactId: null }, false);
        });
    }
    async uploadProfilePhoto(file) {
        const patientId = this.authManager.id;
        if (!patientId)
            return;
        if (file.size > PatientProfileViewModel.MAX_AVATAR_SIZE_BYTES) {
            this.bannerUIManager.displayBanner({ text: t("File size must be 16 MB or less"), type: "ERROR" });
            return;
        }
        runInAction(() => {
            this.isAvatarUploading = true;
        });
        try {
            const uploadInfo = await this.patientService.getPatientAvatarUploadUrl(patientId, file);
            await this.provisioningService.uploadFileToUrl(uploadInfo.signedUrl, file);
            if (uploadInfo.fileUrl) {
                await this.patientService.updatePatient(patientId, { avatarUrl: uploadInfo.fileUrl });
            }
            try {
                await this.authManager.refreshTokenInfo();
            }
            catch (e) {
                console.error("Failed to refresh token after avatar upload", e);
            }
        }
        catch (e) {
            console.error("Failed to upload profile photo", e);
            this.bannerUIManager.displayBanner({ text: t("Failed to upload your profile photo. Please try again"), type: "ERROR" });
        }
        finally {
            runInAction(() => {
                this.isAvatarUploading = false;
            });
        }
    }
    goToTermsOfUse() {
        window.open(buildWebsiteUrl(WebsiteURL.TERMS, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    goToPrivacyPolicy() {
        window.open(buildWebsiteUrl(WebsiteURL.PRIVACY, this.environment.WEBSITE_BASE_URL, i18nGetCurrentLanguage()));
    }
    async logout() {
        await this.appUIManager.logout();
    }
    logoOnClick() {
        var _a;
        if (((_a = this.routingUIManager.currentRoute) === null || _a === void 0 ? void 0 : _a.routeName) === NamedRoutes.PATIENT_ACCOUNT_DELETE_CONFIRMATION.routeName) {
            this.routingUIManager.navigateTo(NamedRoutes.LOGIN_AS_PATIENT);
        }
        else {
            this.appUIManager.navigateToHome();
        }
    }
    clearSmsOptInPollApi() {
        if (this.smsOptInPollApiTimerId !== null) {
            window.clearTimeout(this.smsOptInPollApiTimerId);
        }
    }
    calculateSmsOptInPollInterval(retries) {
        if (retries === 0)
            return 1000;
        if (retries === 1)
            return 10000;
        if (retries === 2)
            return 7500;
        if (retries < 10)
            return 5000;
        if (retries < 20)
            return 7500;
        if (retries < 30)
            return 10000;
        return 12000;
    }
    smsOptInPollApi(retries = 0) {
        this.clearSmsOptInPollApi();
        this.smsOptInPollApiTimerId = window.setTimeout(async () => {
            await this.patientPreferencesManager.preferences.waitForReload();
            if (!this.smsOptedOut) {
                this.smsOptInDialogModel.onChoice(true);
            }
            else {
                this.smsOptInPollApi(retries + 1);
            }
        }, this.calculateSmsOptInPollInterval(retries));
    }
    clearSmsOptInDialogOpenLimit() {
        if (this.smsOptInDialogOpenLimitTimerId !== null) {
            window.clearTimeout(this.smsOptInDialogOpenLimitTimerId);
        }
    }
    smsOptInDialogOpenLimit() {
        this.smsOptInDialogOpenLimitTimerId = window.setTimeout(async () => {
            this.smsOptInDialogModel.onChoice(false);
        }, 60000 * 15);
    }
    async deleteMyAccount() {
        this.routingUIManager.navigateTo(NamedRoutes.PATIENT_DELETE_MY_ACCOUNT);
    }
}
PatientProfileViewModel.MAX_AVATAR_SIZE_BYTES = 16 * 1024 * 1024;
__decorate([
    observable
], PatientProfileViewModel.prototype, "isSavingContactSelection", void 0);
__decorate([
    observable
], PatientProfileViewModel.prototype, "isAvatarUploading", void 0);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showLogout", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "orgName", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "name", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "userForAvatar", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "email", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phone", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "preferredContactMethodRemote", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isOptedIn", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "smsOptedOut", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phoneNumberSuffix", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "twilioFromPhoneNumber", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "showPreferredContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "isClearLogin", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "contactsLoading", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "emails", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "phones", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "hasMultipleContacts", null);
__decorate([
    computed
], PatientProfileViewModel.prototype, "currentSelectedContactId", null);
